import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LoginRequest, User } from '@/types/auth';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  setToken: (token: string, refreshToken: string) => void;
  me: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      login: async (credentials) => {
        const { apiClient } = await import('@/services/api-client');
        const { access, refresh, user } = await apiClient.login(credentials);
        set({ user, token: access, refreshToken: refresh });
      },
      logout: async () => {
        const { token, refreshToken } = get();
        try {
          if (token && refreshToken) {
            const { apiClient } = await import('@/services/api-client');
            await apiClient.logout(refreshToken);
          }
        } catch {
          // API failure does not block local logout
        } finally {
          set({ user: null, token: null, refreshToken: null });
        }
      },
      setToken: (token: string, refreshToken: string) => set({ token, refreshToken }),
      me: async () => {
        const { token } = get();
        if (!token) return;
        
        try {
          const { apiClient } = await import('@/services/api-client');
          const user = await apiClient.me();
          set({ user });
        } catch (error) {
          console.error("Failed to fetch user data:", error);
          // Handle error, e.g., by logging out the user
          set({ user: null, token: null, refreshToken: null });
        }
      },
    }),
    { name: 'auth-storage' }
  )
);
